// Runs compiled programs in the web runtime's page (web/shell/index.html
// ?embed=1), in a frame on another site. A program that never yields then
// runs in another process and cannot freeze the IDE, and removing its frame
// stops it.
//
// The run site is chosen by runOriginFor(n): n is 0 for the usual site, and
// counts fresh sites. A program that never yields keeps its process busy
// after its frame is removed, and the browser may put the next frame on the
// same site into that process until it ends it. So when a stopped program had
// stopped answering pings, the next run goes to a fresh site, at the cost of
// loading the runtime again.

// program.b3dpak: "B3DPAK1\0", a file count, then each file's path length,
// size, UTF-8 path and contents (little-endian), as blitzcc writes it
// (writeAssetPackage in blitz/web_export.cpp), leaving out what it leaves out
// of a web site (skipPackageAsset in blitz/program_sources.cpp).
//
// By name: the program's sources, hidden files, Windows programs and build
// products, scripts, application bundles, the shader compilers' working files,
// their Metal compilations, and Blip's TODO notes (blip/todo.js). A folder so
// named is left out with everything in it.
const LEFT_OUT = [".exe", ".dll", ".pdb", ".ilk", ".app", ".ps1", ".bat", ".bb", ".cso", ".metal-compiled", ".msl"];
const leftOut = (name) => {
  name = name.toLowerCase();
  const extension = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  return !name || name.startsWith(".") || name === "todo" || LEFT_OUT.includes(extension) || name.endsWith(".sources.json") ||
    name.endsWith(".metal-sources.json") || name.includes(".tmp.");
};
export const skipAsset = (path) => path.split("/").some(leftOut);

// A single-file web build is known by the element that holds its program's
// description, in its first 8 KB. What a Blob holds is kept: a project's
// files are Blobs, replaced when written.
const DESCRIPTION = '<script type="application/json" id="b3d-embedded">';
const pages = new WeakMap();
const isWebPage = async (contents) => {
  if (contents instanceof Blob && pages.has(contents)) return pages.get(contents);
  const head = contents instanceof Blob ? new Uint8Array(await contents.slice(0, 8192).arrayBuffer()) :
    typeof contents === "string" ? new TextEncoder().encode(contents.slice(0, 8192)).subarray(0, 8192) :
    contents.subarray(0, 8192);
  const found = new TextDecoder().decode(head).includes(DESCRIPTION);
  if (contents instanceof Blob) pages.set(contents, found);
  return found;
};

// Earlier web builds among the files, such as a download kept with the
// program, which blitzcc never packs either (isWebBuild in
// blitz/web_export.cpp): single-file web builds, and the folders that hold
// both program.b3dpak and runtime.wasm, with everything in them. As for
// blitzcc, whose asset root is the program's folder, the folder of the main
// file (a path in files) is never one, nor are the folders it is in.
// files: [[path, Blob, Uint8Array or string], ...]. Returns a Set of their
// paths. Builds leave them out of the package, and the compiler is not given
// their text.
export async function earlierWebBuilds(files, main = "") {
  // Names compare as on Windows and the Mac, without case.
  const sites = new Map();
  for (const [path] of files) {
    const lower = path.toLowerCase(), slash = lower.lastIndexOf("/");
    const name = lower.slice(slash + 1);
    if (slash < 0 || (name !== "program.b3dpak" && name !== "runtime.wasm")) continue;
    const folder = lower.slice(0, slash + 1);
    sites.set(folder, (sites.get(folder) || 0) | (name === "runtime.wasm" ? 2 : 1));
  }
  const program = (main || "").toLowerCase();
  const folders = [...sites].filter(([folder, found]) => found === 3 && !program.startsWith(folder))
    .map(([folder]) => folder);
  const builds = new Set();
  for (const [path, contents] of files) {
    const lower = path.toLowerCase();
    if (folders.some((folder) => lower.startsWith(folder)) || (/\.html?$/.test(lower) && await isWebPage(contents))) {
      builds.add(path);
    }
  }
  return builds;
}

// Packs files: [[path, Blob, Uint8Array or string], ...], of a program whose
// main file is main. Returns an ArrayBuffer.
// keep(path) keeps a file that would be left out, as the World Editor's run
// keeps the code of the world's own behaviours.
export async function packAssets(files, main = "", keep = () => false) {
  const encoder = new TextEncoder();
  const left = (path) => skipAsset(path) && !keep(path);
  const builds = await earlierWebBuilds(files.filter(([path]) => !left(path)), main);
  const entries = [];
  for (const [path, contents] of files) {
    if (left(path) || builds.has(path)) continue;
    const bytes = typeof contents === "string" ? encoder.encode(contents) :
      contents instanceof Blob ? new Uint8Array(await contents.arrayBuffer()) : contents;
    entries.push([path, encoder.encode(path), bytes]);
  }
  entries.sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  const size = 12 + entries.reduce((total, [, path, contents]) => total + 8 + path.length + contents.length, 0);
  const bytes = new Uint8Array(size), view = new DataView(bytes.buffer);
  bytes.set(encoder.encode("B3DPAK1\0"));
  view.setUint32(8, entries.length, true);
  let offset = 12;
  for (const [, path, contents] of entries) {
    view.setUint32(offset, path.length, true);
    view.setUint32(offset + 4, contents.length, true);
    bytes.set(path, offset + 8);
    bytes.set(contents, offset + 8 + path.length);
    offset += 8 + path.length + contents.length;
  }
  return bytes.buffer;
}

import { ProgramWindow } from "./programwindow.js";
import { SITE_VERSION } from "./site.js";

export class Runner {
  // pane: the element the frame fills. runPath: the run page's path.
  // events: { started(), output(lines), firstFrame(),
  //           ended(status, message, location), responding(isResponding),
  //           debug(event), file(path, bytes), close(), float(floating),
  //           pageMessage(message, file) },
  // location being a runtime error's { file, line } in a debug build, and
  // event one of the debugger's (web/runtime/web_debug_agent.h).
  //
  // A frame that only ever fills its pane, such as the model viewer's
  // (modelviewer.js), is pinned: its programs never take a window of their
  // own or the full screen. A transparent one shows the pane through where
  // the program draws nothing (?transparent=1 on the run page).
  constructor({ pane, runOriginFor, runPath, events, pinned = false, transparent = false }) {
    this.pane = pane;
    this.runOriginFor = runOriginFor;
    this.runPath = runPath;
    this.events = events;
    this.transparent = transparent;
    this.siteNumber = 0;
    this.origin = runOriginFor(0);
    this.frame = null;
    // Requests to the program awaiting its answer (request), by id.
    this.requests = new Map();
    this.requestCount = 0;
    // A windowed program's window, or full screen (programwindow.js).
    this.window = pinned ? { attach() {}, show() {}, reset() {}, state: "pane" } :
      new ProgramWindow({ pane, onClose: () => this.events.close && this.events.close(),
        onFloat: (floating) => this.events.float && this.events.float(floating) });
  }

  get running() { return !!this.frame && !this.ended; }

  // Runs program.wasm with its asset package (packAssets), starting in
  // launchDirectory (the main file's folder in the package, or "."). The files
  // the program writes are kept under storageKey, if one is given. A debug
  // build run with attach serves the IDE's debugger: its events go to
  // events.debug, and debugCommand sends it commands.
  //
  // A tool, such as the World Editor, runs over the project's files: tool
  // names { commandLine, assets: [{ root, assets }] of its own files,
  // environment }, and each file it writes in the project goes to
  // events.file, with null bytes for one it deletes.
  //
  // commandLine is what CommandLine$() returns, for a program; a tool names
  // its own.
  //
  // focus gives the program the keyboard when it starts. A restart while the
  // user types (Live, in main.js) leaves it where it is.
  //
  // environment sets variables for any run, as a tool's does: Blip's test
  // runs (blip/tools.js) set an automatic capture's, whose frame comes back
  // to events.ended. openUrls false stops the program opening web pages.
  run({ title, debug, wasm, assets, launchDirectory = ".", storageKey = null, attach = false, tool = null,
    commandLine = "",
    focus = true, environment = null, openUrls = true }) {
    this.stop();
    const origin = this.origin;
    const frame = this.frame = document.createElement("iframe");
    frame.title = "Program";
    frame.allow = `autoplay ${origin}; fullscreen ${origin}; gamepad ${origin}; clipboard-write ${origin}`;
    // The site's version: the run page loads its own runtime, not one the
    // browser kept from an older publish.
    frame.src = `${origin}${this.runPath}?embed=1${this.transparent ? "&transparent=1" : ""}` +
      `${SITE_VERSION ? `&v=${SITE_VERSION}` : ""}`;
    this.window.attach(frame, focus);
    this.ended = false;
    this.lastPong = performance.now();
    let responding = true;
    // A program that stops answering is shown as not responding.
    this.pingTimer = setInterval(() => {
      frame.contentWindow.postMessage({ type: "b3d-ping" }, origin);
      const now = !this.ended && performance.now() - this.lastPong > 1500 ? false : true;
      if (now !== responding) this.events.responding(responding = now);
    }, 250);
    // Output arrives through a port, which delivers while the program runs.
    const channel = new MessageChannel();
    this.port = channel.port1;
    const receive = (message) => {
      if (!message || this.frame !== frame) return;
      if (message.type === "b3d-pong") {
        this.lastPong = performance.now();
      } else if (message.type === "b3d-frame-ready") {
        frame.contentWindow.postMessage({
          type: "b3d-run",
          program: { title, launchDirectory, commandLine: tool && tool.commandLine || commandLine, debug },
          assets,
          wasm,
          port: channel.port2,
          persist: !!storageKey,
          storageKey,
          debug: debug && attach,
          toolAssets: tool ? tool.assets : [],
          environment: { ...(tool ? tool.environment : {}), ...(environment || {}) },
          openUrls,
          syncFiles: !!tool,
          focus,
        }, origin, [channel.port2]);
        // Keys go to the program once it runs.
        if (focus) frame.focus();
        this.events.started();
      } else if (message.type === "b3d-output") {
        this.events.output(message.lines);
      } else if (message.type === "b3d-first-frame") {
        this.events.firstFrame();
      } else if (message.type === "b3d-graphics") {
        this.window.show(message);
      } else if (message.type === "b3d-file") {
        if (this.events.file) this.events.file(message.path, message.data);
      } else if (message.type === "b3d-debug-event") {
        if (this.events.debug) this.events.debug(message.event);
      } else if (message.type === "b3d-page-message") {
        this.pageMessageReceived(message.message, message.file || null);
      } else if (message.type === "b3d-program-ended") {
        this.ended = true;
        this.endRequests("ended");
        // Its last frame shows in the pane.
        this.window.reset();
        this.events.ended(message.status, message.message || "", message.location || null, message.capture || null);
      }
    };
    channel.port1.onmessage = (event) => receive(event.data);
    this.onWindowMessage = (event) => {
      if (event.source === frame.contentWindow && event.origin === origin) receive(event.data);
    };
    window.addEventListener("message", this.onWindowMessage);
    this.pane.replaceChildren(frame);
  }

  // Changes a live build's numbers while it runs: writes [{ offset, type,
  // value }], offset being in the program's data (docs/ONLINE_IDE_LIVE_CODE.md).
  liveNumbers(writes) {
    if (this.frame && !this.ended) this.port.postMessage({ type: "b3d-live-numbers", writes });
  }

  // Sends the debugger a command line (the protocol of
  // apple/runtime/debug_agent.h).
  debugCommand(line) {
    if (this.frame && !this.ended) this.port.postMessage({ type: "b3d-debug", command: line });
  }

  // Gives the running program a file under /app, where the project's files
  // are, as if it had started with it: one the project gained since it
  // started, such as a model Blip had made while the World Editor was open.
  // Returns whether a program was running to take it.
  putFile(path, bytes) {
    if (!this.frame || this.ended) return false;
    const data = bytes.slice();
    this.port.postMessage({ type: "b3d-put-file", path, data }, [data.buffer]);
    return true;
  }

  // Takes a file under /app away from the running program, as one the
  // project lost since it started. Returns whether a program was running.
  removeFile(path) {
    if (!this.frame || this.ended) return false;
    this.port.postMessage({ type: "b3d-remove-file", path });
    return true;
  }

  // Sends the program a message, which its PageMessage() reads while it
  // yields. Returns whether a program was running to send it to.
  pageMessage(message) {
    if (!this.frame || this.ended) return false;
    this.port.postMessage({ type: "b3d-page-message", message });
    return true;
  }

  // Sends the program a request, an object to which this adds an id, and
  // resolves with its answer, the message with the same id it sends back
  // (SendPageMessage): { message, file }, file being { path, data } when it
  // sent a file's bytes too. Rejects with a RequestError whose code is
  // "not-running", "timeout" (no answer within timeout ms) or "ended" (the
  // program ended or was stopped first). The program's other messages go to
  // events.pageMessage(message, file).
  request(message, timeout = 30000) {
    if (!this.frame || this.ended) return Promise.reject(new RequestError("not-running"));
    const id = `ide-${++this.requestCount}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.requests.delete(id);
        reject(new RequestError("timeout"));
      }, timeout);
      this.requests.set(id, { resolve, reject, timer });
      this.port.postMessage({ type: "b3d-page-message", message: { ...message, id } });
    });
  }

  pageMessageReceived(message, file) {
    const id = message && typeof message === "object" ? message.id : undefined;
    const waiting = id !== undefined && this.requests.get(id);
    if (waiting) {
      this.requests.delete(id);
      clearTimeout(waiting.timer);
      waiting.resolve({ message, file });
    } else if (this.events.pageMessage) {
      this.events.pageMessage(message, file);
    }
  }

  // Rejects every request still waiting, when the program ends.
  endRequests(code) {
    for (const waiting of this.requests.values()) {
      clearTimeout(waiting.timer);
      waiting.reject(new RequestError(code));
    }
    this.requests.clear();
  }

  // Removes the frame, which stops the program. Returns whether one ran.
  stop() {
    if (!this.frame) return false;
    this.endRequests("ended");
    window.removeEventListener("message", this.onWindowMessage);
    clearInterval(this.pingTimer);
    this.window.reset();
    this.frame.remove();
    this.frame = null;
    this.port = null;
    if (!this.ended && performance.now() - this.lastPong > 1000) this.origin = this.runOriginFor(++this.siteNumber);
    return true;
  }

  // Full screen for the frame, from the IDE's own click; the program keeps
  // the keyboard.
  fullscreen() {
    const frame = this.frame;
    if (frame) frame.requestFullscreen().then(() => frame.focus(), () => {});
  }
}

// Why a request to the program (Runner.request) has no answer: code is
// "not-running", "timeout" or "ended".
export class RequestError extends Error {
  constructor(code) {
    super(code === "timeout" ? "The program did not answer in time." :
      code === "ended" ? "The program ended before it answered." : "No program is running.");
    this.code = code;
  }
}
